import type { IDecision } from '@/services/DecisionApi';
import { originatingIndexOf, type IOriginatingEntry } from '@/shell/originatingList';

/**
 * F-0567 (NX-5, NX-7; `docs/concept-decisions-and-questions.md` §5c-ii): which decision is next
 * after an answer, and how many still wait.
 *
 * The next one is the one after this one in the list the person came from (`setOriginatingList`,
 * IU-9): that list's filter, order and scope, wrapping to its top after the last row. A waiting
 * decision the list does not show is not next. With no list recorded for this address (a link, a
 * reload), it is the first in the waiting set's own order, which is the Decisions list's. A
 * decision taken back in this session is skipped; a postponed one is never in the waiting set.
 */
export interface INextWaitingDecision {
  readonly next: IDecision | null;
  /**
   * The address the list's own row opens `next` at (I-1264: never derived from a key), so the run
   * stays in that list — Items stays Items; `null` with no list, where the caller builds one.
   */
  readonly href: string | null;
  /** The decisions still waiting in that order, `next` included. */
  readonly stillWaiting: number;
}

export function nextWaitingDecision({
  answered,
  waiting,
  entries,
  currentPath,
  workAreaOf,
  workAreaRefOf,
  skipped = () => false,
}: {
  answered: { readonly key: string; readonly workAreaId: string };
  /** The waiting set the Decisions list and its badge read (`decisionsStore.waiting`). */
  waiting: readonly IDecision[] | null;
  /** The list the decision was opened from, or `null` with none. */
  entries: readonly IOriginatingEntry[] | null;
  /** The address the answered decision is shown at, to find it in `entries`. */
  currentPath: string;
  workAreaOf: (decision: IDecision) => string;
  /** The work area's route ref, which tells two rows of one key in different work areas apart. */
  workAreaRefOf: (decision: IDecision) => string | null;
  skipped?: (decision: IDecision) => boolean;
}): INextWaitingDecision {
  const candidates = (waiting ?? []).filter((decision) => {
    const area = workAreaOf(decision);
    if (decision.item.key === answered.key && area === answered.workAreaId) return false;
    return !skipped(decision);
  });
  const at = entries ? originatingIndexOf(entries, currentPath) : -1;
  if (!entries || at === -1) {
    return { next: candidates[0] ?? null, href: null, stillWaiting: candidates.length };
  }
  const ordered = [...entries.slice(at + 1), ...entries.slice(0, at)];
  const queue: IDecision[] = [];
  const hrefs: string[] = [];
  for (const entry of ordered) {
    const sameKey = candidates.filter((decision) => decision.item.key === entry.key);
    const match =
      sameKey.length <= 1
        ? sameKey[0]
        : sameKey.find((decision) => {
            const ref = workAreaRefOf(decision);
            return ref !== null && entry.href.includes(`/${ref}/`);
          });
    if (match && !queue.includes(match)) {
      queue.push(match);
      hrefs.push(entry.href);
    }
  }
  return { next: queue[0] ?? null, href: hrefs[0] ?? null, stillWaiting: queue.length };
}

/**
 * F-0567 (NX-7, I-1604): the list row's own address for the next decision, keeping the query the
 * list is drawn with — its search (`q`), filters and sort — so the list beside the next decision is
 * still the list it came from. The row's own parameters (its `focus`) win over the page's.
 */
export function addressKeepingListQuery(href: string, currentSearch: string): string {
  const [path, own = ''] = href.split('?', 2);
  const params = new URLSearchParams(currentSearch);
  for (const [name, value] of new URLSearchParams(own)) params.set(name, value);
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

/** F-0567 (NX-5): where a run of answers is, carried in the step's history state. */
export interface IDecisionQueuePosition {
  /** The decision's place in the run, 1 for the first. */
  readonly position: number;
  /** How many the run holds: those answered in it and those still waiting. */
  readonly total: number;
}

const QUEUE_STATE_KEY = 'decisionQueue';

/** The run's position as an address's history state carries it, or `null`. */
export function decisionQueueOf(state: unknown): IDecisionQueuePosition | null {
  if (typeof state !== 'object' || state === null) return null;
  const queue = (state as Record<string, unknown>)[QUEUE_STATE_KEY];
  if (typeof queue !== 'object' || queue === null) return null;
  const { position, total } = queue as Record<string, unknown>;
  if (typeof position !== 'number' || typeof total !== 'number') return null;
  if (!Number.isInteger(position) || position < 1 || total < position) return null;
  return { position, total };
}

/**
 * The position of the decision the step opens: one after the answered one (1 when no run was in
 * progress), out of the answered one's place plus all still waiting.
 */
export function decisionQueueAfter(
  run: IDecisionQueuePosition | null,
  stillWaiting: number,
): IDecisionQueuePosition {
  const answeredAt = run?.position ?? 1;
  return { position: answeredAt + 1, total: answeredAt + stillWaiting };
}

/** The history state a step carries the run's position in, beside the step's own mark. */
export function withDecisionQueue(
  state: Record<string, unknown>,
  queue: IDecisionQueuePosition,
): Record<string, unknown> {
  return { ...state, [QUEUE_STATE_KEY]: queue };
}

/** The quiet line's words for it. */
export function decisionQueueLabel(queue: IDecisionQueuePosition): string {
  return `${queue.position} of ${queue.total} waiting`;
}

// Taken back in this session (NX-7): the answer was withdrawn by the person, so the run moves past
// it rather than offering it straight back. Kept for the page's life, as the run is.
const takenBack = new Set<string>();

/** Called with the decision's item id when its answer is taken back, from the card or the toast. */
export function noteAnswerTakenBack(itemId: string): void {
  takenBack.add(itemId);
}

export function answerTakenBackThisSession(decision: IDecision): boolean {
  return takenBack.has(decision.item.id);
}

/** For tests. */
export function resetAnswersTakenBack(): void {
  takenBack.clear();
}
